import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  AlertController,
  MenuController,
  ModalController,
  NavController,
} from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import LABELS from "src/app/constants/LABELS";
import { FriendFormPage } from "src/app/modals/friend-form/friend-form.page";
import { ProjectFormPage } from "src/app/modals/project-form/project-form.page";
import { AuthService } from "src/app/services/auth/auth.service";
import { CommonService } from "src/app/services/common/common.service";
import { FriendService } from "src/app/services/friend/friend.service";
import { TaskService } from "src/app/services/task/task.service";
import { ProjectService } from "src/app/services/project/project.service";
import {
  friendCreate,
  friendDelete,
  friendList,
} from "src/app/store/friend/friend.action";
import { selectorFriend } from "src/app/store/friend/friend.selector";
import { projectCreate, projectList } from "src/app/store/project/project.action";
import { selectorProject } from "src/app/store/project/project.selector";
import { DEFAULT_CONFIGURATIONS } from "src/app/constants/DEFAULT_CONFIGURATIONS";

@Component({
  selector: "app-menu",
  templateUrl: "./menu.component.html",
  styleUrls: ["./menu.component.scss"],
  providers: [NavController, ModalController],
})
export class MenuComponent implements OnInit {
  @Input() active = "home";
  @Output() onNew = new EventEmitter();

  logged: any = {};
  totalNewNotifications: any = 0;
  totalTasks: any = 0;

  projects: any = [];
  friends: any = [];
  totalFriends: any = 0;

  appConfigurations: any = DEFAULT_CONFIGURATIONS;

  constructor(
    private auth: AuthService,
    private taskService: TaskService,
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private menuCtrl: MenuController,
    private commonService: CommonService,
    private store: Store,
    private friendService: FriendService,
    private alertController: AlertController,
    private translate: TranslateService,
    private projectService: ProjectService
  ) {
    this.store.select(selectorFriend).subscribe((state) => {
      this.friends = state.items;
    });

    this.store.select(selectorProject).subscribe((state: any) => {
      this.projects = state?.items || [];
    });
  }

  ngOnInit() {
    this.logged = this.auth.getLoggedUser() || {};

    if (this.logged) {
      this.appConfigurations = this.logged.configurations
        ? JSON.parse(this.logged.configurations)
        : DEFAULT_CONFIGURATIONS;
    }

    // Menu labels are client-only; do not PATCH the Account API (schema has no `labels` field).
    this.logged.labels = LABELS;
    this.auth.setLoggedUser(this.logged);

    this.getFriends();
    this.getProfiles();
    this.getProjects();

    setInterval(() => {
      this.logged = this.auth.getLoggedUser() || {};
    }, 5000);

    setInterval(() => {
      this.getProfiles();
    }, 30000);
  }

  getFriends() {
    this.friendService
      .list({
        user: this.logged.id,
        $page: 1,
        $limit: 99,
        // $sort: { title: 1 },
        $populate: ["friend", "user"],
      })
      .subscribe((resp) => {
        this.totalFriends = resp.total;
        this.store.dispatch(friendList({ items: resp.data }));
      });
  }

  getProjects() {
    this.projectService
      .list({
        $page: 1,
        $limit: 99,
        // projects table may not have sortOrder until migration; title is always present
        $sort: {
          title: 1,
        },
      })
      .subscribe((resp: any) => {
        this.store.dispatch(projectList({ items: resp?.data || [] }));
      });
  }

  getProfiles(id = null) {
    const accessToken = window.localStorage.getItem("accessToken");
    if (!accessToken) {
      return;
    }

    this.taskService
      .list({
        $page: 1,
        $limit: Number.MAX_SAFE_INTEGER,
        status: "new",
      })
      .subscribe((resp) => {
        this.totalTasks = resp.total;
      });
  }

  changePage($page) {
    this.navCtrl.navigateRoot($page, { replaceUrl: true });
    this.menuCtrl.close();
  }

  async openProjectFormModal() {
    // Close menu before modal: ion-menu backdrop/gesture can capture touches on mobile.
    await this.menuCtrl.close();

    const modal: any = await this.modalCtrl.create({
      component: ProjectFormPage,
    });

    modal.onDidDismiss().then(($event: any) => {
      if ($event.data) {
        this.store.dispatch(projectCreate({ item: $event.data }));
        // this.onNew.emit($event.data);
      }
    });
    return await modal.present();
  }

  async openFriendFormModal() {
    if (this.totalFriends >= this.appConfigurations.maxOfFiends) {
      this.commonService.showMessage(
        this.translate.instant("You have reached the maximum number of friends")
      );
      return;
    }

    await this.menuCtrl.close();

    const modal: any = await this.modalCtrl.create({
      component: FriendFormPage,
    });

    modal.onDidDismiss().then(($event: any) => {
      if ($event.data) {
        this.store.dispatch(friendCreate({ item: $event.data }));
        // this.onNew.emit($event.data);
      }
    });
    return await modal.present();
  }

  goTo(url) {
    this.navCtrl.navigateRoot(url, { replaceUrl: true });
    this.menuCtrl.close();
  }

  logout() {
    this.commonService.confirm(() => {
      this.auth.signOut();
      this.menuCtrl.close();
      this.navCtrl.navigateRoot("/login", { replaceUrl: true });
    });
  }

  async deleteFriend(friend) {
    const alert = await this.alertController.create({
      cssClass: "my-custom-class",
      header: this.translate.instant("Delete Your Friend"),
      message: this.translate.instant(
        "Are you sure to want to delete this friend from your list?"
      ),
      buttons: [
        {
          text: this.translate.instant("Cancel"),
          role: "cancel",
          cssClass: "secondary",
          handler: (blah) => {
            console.log("Confirm Cancel: blah");
          },
        },
        {
          text: this.translate.instant("Confirm"),
          handler: () => {
            this.friendService.delete(friend).subscribe((resp) => {
              this.store.dispatch(friendDelete({ item: resp }));
            });
          },
        },
      ],
    });

    await alert.present();
  }

  currentLanguage = window.localStorage.getItem("language") || "en";
  changeLanguage(lang = "en") {
    this.translate.use(lang);
    this.currentLanguage = lang;
    window.localStorage.setItem("language", lang);

    console.log(this.currentLanguage);
  }
}
